import { useEffect, useState } from "react";
import { Mail, Loader2, Sparkles, Copy, Send, CheckCircle2, Edit3, RefreshCw } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";

/**
 * Carte affichant le draft d'email IA généré automatiquement quand un lead passe en HOT.
 *
 * Workflow :
 *   - Si pas de draft → affiche un bouton "Générer"
 *   - Si draft existe → affiche subject + body éditables
 *   - Boutons "Copier", "Marquer envoyé", "Régénérer"
 */
export default function EmailDraftCard({ api, prospectId, prospectEmail, testId = "email-draft-card" }) {
  const [draft, setDraft] = useState(null);
  const [aiEvaluation, setAiEvaluation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [marking, setMarking] = useState(false);
  const [editedSubject, setEditedSubject] = useState("");
  const [editedBody, setEditedBody] = useState("");
  const [copied, setCopied] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/crm/prospects/${prospectId}/email-draft`);
      setDraft(r.data?.draft || null);
      setAiEvaluation(r.data?.ai_evaluation || null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!prospectId) return;
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prospectId]);

  const generate = async () => {
    setGenerating(true);
    try {
      const r = await api.post(`/crm/prospects/${prospectId}/email-draft/regenerate`);
      setDraft(r.data);
    } catch (e) {
      alert(e.response?.data?.detail || "Erreur génération draft");
    } finally {
      setGenerating(false);
    }
  };

  const startEdit = () => {
    setEditedSubject(draft.subject || "");
    setEditedBody(draft.body || "");
    setEditing(true);
  };

  const saveEdit = async () => {
    try {
      const r = await api.put(`/crm/prospects/${prospectId}/email-draft`, {
        subject: editedSubject,
        body: editedBody,
      });
      setDraft(r.data);
      setEditing(false);
    } catch (e) {
      alert(e.response?.data?.detail || "Erreur sauvegarde");
    }
  };

  const copyAll = () => {
    const text = `Sujet: ${draft.subject}\n\n${draft.body}`;
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const openMailto = () => {
    if (!prospectEmail) {
      alert("Pas d'email enregistré pour ce prospect.");
      return;
    }
    const url = `mailto:${prospectEmail}?subject=${encodeURIComponent(draft.subject || "")}&body=${encodeURIComponent(draft.body || "")}`;
    window.open(url, "_blank");
  };

  const markSent = async () => {
    if (!window.confirm("Marquer cet email comme envoyé ? Une activity 'Email' sera créée dans la timeline.")) return;
    setMarking(true);
    try {
      const r = await api.post(`/crm/prospects/${prospectId}/email-draft/mark-sent`);
      setDraft(r.data);
    } finally {
      setMarking(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-[#0b0811] rounded-xl border border-white/5 p-4" data-testid={`${testId}-loading`}>
        <div className="flex items-center gap-2 text-slate-400 text-sm">
          <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
        </div>
      </div>
    );
  }

  if (!draft) {
    if (!aiEvaluation) {
      return null; // Pas évalué : ne rien afficher
    }
    return (
      <div className="bg-violet-500/5 border border-dashed border-violet-500/30 rounded-xl p-5 text-center" data-testid={`${testId}-empty`}>
        <Mail className="w-8 h-8 text-violet-400 mx-auto mb-3" />
        <h3 className="text-sm font-bold text-white mb-1">Email IA personnalisé</h3>
        <p className="text-xs text-slate-400 mb-4">
          Générez automatiquement un draft d'email de prise de RDV personnalisé d'après le profil IA de ce prospect.
        </p>
        <Button onClick={generate} disabled={generating} className="bg-violet-500 text-white hover:bg-violet-600 text-xs font-bold">
          {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
          Générer un draft
        </Button>
      </div>
    );
  }

  const isSent = draft.status === "sent";
  const isFallback = draft.source === "fallback" || draft.model === "heuristic";

  return (
    <div className={`rounded-xl border p-4 space-y-3 ${isSent ? "bg-emerald-500/5 border-emerald-500/30" : "bg-violet-500/5 border-violet-500/20"}`} data-testid={testId}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${isSent ? "bg-emerald-500/20" : "bg-violet-500/15"}`}>
            <Mail className={`w-4 h-4 ${isSent ? "text-emerald-400" : "text-violet-400"}`} />
          </div>
          <div>
            <div className="text-sm font-bold text-white flex items-center gap-2">
              Email IA personnalisé
              {isSent && (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 font-bold uppercase">
                  ✓ Envoyé
                </span>
              )}
            </div>
            <div className="text-[10px] text-slate-500">{isFallback ? "⚙️ heuristique" : `🤖 ${draft.model || "Gemini"}`} · ton {draft.tone || "professionnel"}</div>
          </div>
        </div>
        {!isSent && !editing && (
          <div className="flex gap-1">
            <button onClick={generate} disabled={generating} className="text-[11px] px-2 py-1 rounded-md bg-white/5 hover:bg-white/10 text-slate-300 inline-flex items-center gap-1" data-testid="email-regenerate-btn">
              {generating ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Régénérer
            </button>
            <button onClick={startEdit} className="text-[11px] px-2 py-1 rounded-md bg-white/5 hover:bg-white/10 text-slate-300 inline-flex items-center gap-1" data-testid="email-edit-btn">
              <Edit3 className="w-3 h-3" /> Éditer
            </button>
          </div>
        )}
      </div>

      {editing ? (
        <div className="space-y-2">
          <Input
            value={editedSubject}
            onChange={(e) => setEditedSubject(e.target.value)}
            className="bg-[#0b0811] border-white/10 text-white text-sm"
            placeholder="Objet"
            data-testid="email-edit-subject"
          />
          <Textarea
            value={editedBody}
            onChange={(e) => setEditedBody(e.target.value)}
            rows={8}
            className="bg-[#0b0811] border-white/10 text-white text-sm font-mono leading-relaxed"
            placeholder="Corps de l'email"
            data-testid="email-edit-body"
          />
          <div className="flex justify-end gap-2">
            <Button onClick={() => setEditing(false)} variant="outline" size="sm" className="border-white/10 text-slate-300 text-xs">
              Annuler
            </Button>
            <Button onClick={saveEdit} size="sm" className="bg-[#f7c935] text-[#14101c] hover:bg-[#e6b825] text-xs font-bold" data-testid="email-save-btn">
              <CheckCircle2 className="w-3 h-3 mr-1" /> Enregistrer
            </Button>
          </div>
        </div>
      ) : (
        <>
          <div className="bg-[#0b0811] rounded-lg border border-white/5 p-3" data-testid="email-draft-content">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1">Objet</div>
            <div className="text-sm font-semibold text-white">{draft.subject}</div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-1 mt-3">Corps</div>
            <div className="text-sm text-slate-200 whitespace-pre-wrap leading-relaxed">{draft.body}</div>
          </div>

          {!isSent && (
            <div className="flex flex-wrap items-center gap-2 pt-1">
              <Button onClick={copyAll} size="sm" variant="outline" className="border-white/10 text-slate-300 text-xs" data-testid="email-copy-btn">
                {copied ? <CheckCircle2 className="w-3 h-3 mr-1 text-emerald-400" /> : <Copy className="w-3 h-3 mr-1" />}
                {copied ? "Copié !" : "Copier"}
              </Button>
              {prospectEmail && (
                <Button onClick={openMailto} size="sm" className="bg-blue-500 text-white hover:bg-blue-600 text-xs font-bold" data-testid="email-mailto-btn">
                  <Send className="w-3 h-3 mr-1" /> Ouvrir dans ma messagerie
                </Button>
              )}
              <Button onClick={markSent} disabled={marking} size="sm" className="bg-emerald-500 text-white hover:bg-emerald-600 text-xs font-bold" data-testid="email-mark-sent-btn">
                {marking ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <CheckCircle2 className="w-3 h-3 mr-1" />}
                Marquer envoyé
              </Button>
            </div>
          )}

          {isSent && draft.sent_at && (
            <div className="text-[10px] text-emerald-400 italic pt-1">
              Envoyé le {new Date(draft.sent_at).toLocaleString("fr-FR")}
            </div>
          )}
        </>
      )}
    </div>
  );
}
